import { zodResolver } from "@hookform/resolvers/zod";
import LoadingButton from "@mui/lab/LoadingButton";
import { FormControl, Grid } from "@mui/material";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { ControlledSelect } from "../../../components/form/ControlledSelect/ControlledSelect";
import { ControlledTextField } from "../../../components/form/ControlledTextField/ControlledTextField";
import { useNotificationSelectList } from "../../../services/agreementsService";
import { useMemo, useRef } from "react";
import { EmailBodyEditor } from "../../../components/dialog/EmailSender";

const createEmailTemplateFormSchema = (t) =>
    z.object({
        templateNumber: z.string().min(1, t("drafts.validation.templateNumberRequired")),
        templateName: z.string().min(1, t("drafts.validation.templateNameRequired")),
        templateDescription: z.string().min(1, t("drafts.validation.descriptionRequired")),
        subject: z.string().min(1, t("drafts.validation.emailTitleRequired")),
        // body: z.string().min(1, t("drafts.validation.emailContentsRequired")),
        templateLanguage: z.string().min(1, t("drafts.validation.languageRequired")),
        active: z.string().min(1, t("drafts.validation.activeRequired")),
        draftNotification: z.string().optional().nullable()
    });

export function EmailTemplateForm(props) {
    const { t } = useTranslation();
    const emailTemplateFormSchema = createEmailTemplateFormSchema(t);

    const {data: notificationSelectList} = useNotificationSelectList()

    const isDraftForNotification = props.draftType === 'notification'
    
    const bodyRef = useRef(null);

    const { handleSubmit, control, watch } = useForm({
        resolver: zodResolver(emailTemplateFormSchema),
        defaultValues: {
            templateNumber: "",
            templateName: "",
            templateDescription: "",
            subject: "",
            body: "",
            templateLanguage: "",
            active: "",
            draftNotification: null,
            ...props.defaultValues,
        },
    });

    const requiredFields = useMemo(()=>{
        let requiredFieldsTemp = [
            "templateNumber",
            "templateName",
            "templateDescription",
            "subject",
            // "body",
            "templateLanguage",
            "active",
        ]
        if (isDraftForNotification) {
            requiredFieldsTemp.push('draftNotification')
        }

        return requiredFieldsTemp
    }, [isDraftForNotification])

    const submitEnabled = watch(requiredFields).reduce((acc, curr) => acc && Boolean(curr), true) && bodyRef.current?.getHtml()

    const notificationOptions = useMemo(()=>{
        if (!notificationSelectList) {
            return []
        }
        return notificationSelectList.map(notification=>({value: notification.id, label: notification.notification_name}))
    }, [notificationSelectList])

    const languageOptions = [
        { value: "PL", label: t("drafts.languages.PL") },
        { value: "EN", label: t("drafts.languages.EN") },
    ];

    const activeOptions = [
        { value: "true", label: t("yes") },
        { value: "false", label: t("no") },
    ];

    const onSubmit = () => {
        handleSubmit(
            (data)=>{
                data.body = bodyRef.current?.getHtml() || ""
                props.onSubmit(data)
            },
            props.onError
        )();
    };

    return (
        <Grid container direction={"column"} spacing={2} sx={{ pt: 3 }}>
            <FormControl>
                <Grid container direction={"row"} rowSpacing={2} columnSpacing={2}>
                    {isDraftForNotification && <Grid item xs={12}>
                        <ControlledSelect
                            control={control}
                            name="draftNotification"
                            label={`${t("drafts.columns.notificationType")}*`}
                            data={notificationOptions}
                        />
                        </Grid>
                    }
                    <Grid item xs={6}>
                        <ControlledTextField
                            control={control}
                            name="templateNumber"
                            label={`${t("drafts.columns.templateNumber")}*`}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <ControlledSelect
                            control={control}
                            name="templateLanguage"
                            label={`${t("drafts.columns.language")}*`}
                            data={languageOptions}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <ControlledTextField
                            control={control}
                            name="templateName"
                            label={`${t("drafts.templateName")}*`}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <ControlledTextField
                            control={control}
                            name="templateDescription"
                            label={`${t("drafts.description")}*`}
                            props={{
                                InputProps: {
                                    rows: 4,
                                    multiline: true,
                                    inputComponent: "textarea",
                                },
                            }}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <ControlledTextField
                            control={control}
                            name="subject"
                            label={`${t("drafts.emailTemplates.emailTitle")}*`}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <EmailBodyEditor ref={bodyRef} initialText={watch("body")}/>
                        {/* <ControlledTextField
                            control={control}
                            name="body"
                            label={`${t("drafts.emailTemplates.emailContents")}*`}
                            props={{
                                InputProps: {
                                    rows: 6,
                                    multiline: true,
                                    inputComponent: "textarea",
                                },
                            }}
                        /> */}
                    </Grid>

                    <Grid item xs={12}>
                        <ControlledSelect
                            control={control}
                            name="active"
                            label={`${t("drafts.columns.active")}*`}
                            data={activeOptions}
                        />
                    </Grid>
                </Grid>
                <Grid item xs={12} sx={{ mt: 4 }}>
                    <LoadingButton
                        type="submit"
                        variant="contained"
                        color="success"
                        fullWidth
                        disabled={!submitEnabled}
                        // onClick={handleSubmit(props.onSubmit, props.onError)}
                        onClick={onSubmit}
                        sx={{
                            backgroundColor: "#4caf50",
                            "&:hover": {
                                backgroundColor: "#45a049",
                            },
                        }}
                    >
                        {props.submitBtnText}
                    </LoadingButton>
                </Grid>
            </FormControl>
        </Grid>
    );
}
